// PWA Utility Functions

export const DISMISS_KEY = 'appmed-pwa-prompt-dismissed';

// --- Platform detection helpers ---

// Detect iOS (including iOS 13+ iPad that reports as Mac)
export const isIOS = (): boolean => {
    const userAgent = window.navigator.userAgent;
    const isIOSDevice = /iPhone|iPad|iPod/i.test(userAgent);
    // iOS 13+ iPad reports as Mac, check for touch support
    const isIPadOS = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;
    return isIOSDevice || isIPadOS;
};

// Detect macOS (but not iOS pretending to be Mac)
export const isMacOS = (): boolean => {
    const userAgent = window.navigator.userAgent;
    const isMac = /Macintosh/i.test(userAgent);
    // Exclude iPadOS (which reports as Mac but has touch)
    const isNotIPad = navigator.maxTouchPoints <= 1;
    return isMac && isNotIPad;
};

// Detect Android
export const isAndroid = (): boolean => {
    const userAgent = window.navigator.userAgent;
    return /Android/i.test(userAgent);
};

// Detect Safari
export const isSafari = (): boolean => {
    const userAgent = window.navigator.userAgent;
    const isSafariBrowser = /Safari/i.test(userAgent) &&
                           !/Chrome/i.test(userAgent) &&
                           !/CriOS/i.test(userAgent) &&
                           !/FxiOS/i.test(userAgent) &&
                           !/EdgiOS/i.test(userAgent);
    return isSafariBrowser;
};

// Detect Firefox
export const isFirefox = (): boolean => {
    const userAgent = window.navigator.userAgent;
    return /Firefox/i.test(userAgent) || /FxiOS/i.test(userAgent);
};

// Check if running as installed PWA (standalone mode)
export const isInStandaloneMode = (): boolean => {
    // Check iOS standalone mode
    const iosStandalone = 'standalone' in window.navigator && (window.navigator as any).standalone === true;
    // Check display-mode media query
    const displayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
    return iosStandalone || displayModeStandalone;
};

// --- Scenario-based PWA install classification ---

export type PWAInstallScenario =
    | 'native-prompt'    // Browser fires beforeinstallprompt (Chrome, Edge, Opera, Samsung Internet on Android/Desktop)
    | 'ios-safari'       // iOS Safari: manual Share > Add to Home Screen
    | 'ios-non-safari'   // iOS non-Safari or in-app browser: must open in Safari first
    | 'macos-safari'     // macOS Safari: File > Add to Dock (macOS Sonoma+)
    | 'android-manual'   // Android Firefox or other browser: manual menu instructions
    | 'unsupported';     // Desktop Firefox, already standalone, or no PWA support

export const getPWAInstallScenario = (): PWAInstallScenario => {
    // Already running as installed PWA
    if (isInStandaloneMode()) return 'unsupported';

    const userAgent = window.navigator.userAgent;
    const iosDetected = isIOS();
    const androidDetected = isAndroid();

    // --- iOS ---
    if (iosDetected) {
        if (isSafari()) return 'ios-safari';
        // Everything else on iOS: Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS),
        // in-app browsers (Facebook, Instagram, Gmail, etc.)
        return 'ios-non-safari';
    }

    // --- Android ---
    if (androidDetected) {
        // Android WebView (in-app browsers like Facebook, Instagram)
        if (/wv\)/i.test(userAgent)) return 'android-manual';
        // Chrome, Edge, Samsung Internet, Opera — all fire beforeinstallprompt
        if (/Chrome/i.test(userAgent) || /Edg/i.test(userAgent) || /SamsungBrowser/i.test(userAgent) || /OPR/i.test(userAgent))
            return 'native-prompt';
        // Android Firefox and others — manual menu instructions
        return 'android-manual';
    }

    // --- Desktop ---
    // Chrome, Edge, Opera on desktop fire beforeinstallprompt
    if (/Chrome/i.test(userAgent) || /Edg/i.test(userAgent) || /OPR/i.test(userAgent))
        return 'native-prompt';

    // macOS Safari: supports "Add to Dock" (macOS Sonoma+)
    if (isMacOS() && isSafari())
        return 'macos-safari';

    // Desktop Firefox and others — no reliable PWA install
    return 'unsupported';
};

// Returns true when the platform supports any form of PWA installation
export const isPWAInstallable = (): boolean => {
    return getPWAInstallScenario() !== 'unsupported';
};

// Backward-compatible alias
export const isPWASupported = isPWAInstallable;

// Clear the dismiss flag to allow the PWA prompt to show again
export const resetPWAPrompt = (): void => {
    localStorage.removeItem(DISMISS_KEY);
    // Reload the page to trigger the prompt check again
    window.location.reload();
};

// Check if PWA prompt was dismissed
export const wasPWAPromptDismissed = (): boolean => {
    return !!localStorage.getItem(DISMISS_KEY);
};
